import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { fileUrl } from "@/lib/api";

export default function Lightbox({ items, index, onClose, onIndex }) {
  const open = index !== null && index >= 0 && items && items.length > 0;

  const go = useCallback(
    (dir) => {
      if (!open) return;
      const next = (index + dir + items.length) % items.length;
      onIndex(next);
    },
    [open, index, items, onIndex]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, go, onClose]);

  if (!open) return null;
  const item = items[index];

  return (
    <AnimatePresence>
      <motion.div
        key="lightbox"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
        onClick={onClose}
        data-testid="lightbox"
      >
        <button onClick={onClose} data-testid="lightbox-close" className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20">
          <X className="h-5 w-5" />
        </button>

        {items.length > 1 && (
          <>
            <button onClick={(e) => { e.stopPropagation(); go(-1); }} data-testid="lightbox-prev" className="absolute left-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:left-8">
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); go(1); }} data-testid="lightbox-next" className="absolute right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 sm:right-8">
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        <motion.div
          key={item.id || index}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
          className="relative max-h-[85vh] max-w-5xl"
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === "video" ? (
            <video src={fileUrl(item.url)} controls autoPlay playsInline className="max-h-[85vh] max-w-full rounded-xl" />
          ) : (
            <img src={fileUrl(item.url)} alt={item.filename || "Media"} className="max-h-[85vh] max-w-full rounded-xl object-contain" />
          )}
          {item.uploader_name && (
            <p className="mt-3 text-center text-sm text-white/70">Shared by {item.uploader_name}</p>
          )}
        </motion.div>

        {items.length > 1 && (
          <span className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-1.5 text-sm text-white">
            {index + 1} / {items.length}
          </span>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
